import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { StatusBadge, statusLabel } from '@/components';
import { useAuthFetch } from '@/hooks';
import type { DoctorPaymentsResponse, PaymentStatus } from '@dermalink/shared';
import { ROUTES } from '../../routes.ts';
import styles from './Payments.module.css';

function formatEuroFromCents(cents: number): string {
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('nl-BE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function paymentStatusLabel(status: PaymentStatus): string {
  if (status === 'pending') return 'In afwachting';
  if (status === 'paid') return 'Betaald';
  if (status === 'refunded') return 'Teruggestort';
  return 'Niet nodig';
}

function paymentStatusClass(status: PaymentStatus): string {
  if (status === 'pending') return styles.paymentPillPending;
  if (status === 'paid') return styles.paymentPillPaid;
  if (status === 'refunded') return styles.paymentPillRefunded;
  return styles.paymentPillNotRequired;
}

export function Payments() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const { data, isPending, isError } = useQuery<DoctorPaymentsResponse>({
    queryKey: ['doctor-payments'],
    queryFn: async () => {
      const res = await authFetch('/doctor/payments');
      if (!res.ok) throw new Error();
      return (await res.json()) as DoctorPaymentsResponse;
    },
    refetchInterval: 10_000,
  });

  const payments = data?.payments ?? [];

  return (
    <div className={styles.page}>
      <h1 className="page-heading">Betalingen</h1>
      <p className={`text-muted ${styles.subheading}`}>Overzicht van betaalstatussen, Mollie-referenties en timing per consult.</p>

      {isPending && <p className="text-muted status-message">Laden...</p>}
      {isError && <p className="text-muted status-message">Kon betalingen niet laden.</p>}
      {!isPending && !isError && payments.length === 0 && <p className="text-muted status-message">Nog geen betalingen.</p>}

      {!isPending && !isError && payments.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Consult</th>
                <th>Patiënt</th>
                <th>Bedrag</th>
                <th>Betaalstatus</th>
                <th>Gesprekstatus</th>
                <th>Mollie</th>
                <th>Laatst bijgewerkt</th>
                <th>Vervalt</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={`${p.conversation_id}-${p.payment_updated_at}`}>
                  <td>
                    <button
                      type="button"
                      className={styles.linkBtn}
                      onClick={() => navigate(`${ROUTES.doctorConsultations}?conversationId=${p.conversation_id}`)}
                    >
                      #{p.conversation_id}
                    </button>
                  </td>
                  <td>{p.patient_name}</td>
                  <td>{formatEuroFromCents(p.amount_cents)}</td>
                  <td>
                    <span className={`${styles.paymentPill} ${paymentStatusClass(p.payment_status)}`}>
                      {paymentStatusLabel(p.payment_status)}
                    </span>
                  </td>
                  <td><StatusBadge status={p.conversation_status} label={statusLabel(p.conversation_status)} /></td>
                  <td>
                    <span className={styles.mollieId}>
                      {p.mollie_payment_id ?? '—'}
                    </span>
                  </td>
                  <td>{formatDateTime(p.payment_updated_at)}</td>
                  <td>{p.payment_expires_at ? formatDateTime(p.payment_expires_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
